import { SabotageRarity, RARITY_CONFIG } from '@/types/sabotageStats';

export { type SabotageRarity, RARITY_CONFIG } from '@/types/sabotageStats';

export interface DraftSabotageConfig {
  id: string;
  name: string;
  description: string;
  effect: string;
  cost: number;
  rarity: SabotageRarity;
  icon: string;
  color: 'damage' | 'primary' | 'warning' | 'success';
  isActive: boolean;
  /** When false, card shows in UI but is grayed out and non-selectable (beta toggle) */
  enabled?: boolean;
  timesUsed: number;
  totalCostCollected: number;
  createdAt: Date;
}

/** Suggested cost per rarity tier */
export const RARITY_COST_GUIDE: Record<SabotageRarity, number> = {
  common: 5,
  rare: 15,
  legendary: 35,
};

export interface DraftSabotageSettings {
  maxActive: number;
  maxInactive: number;
  maxPerDraft: number;
  sabotages: DraftSabotageConfig[];
}

export const defaultDraftSabotages: DraftSabotageConfig[] = [
  {
    id: 'ds1', name: 'Half Time',
    description: "Cut opponent's draft timer in half for their next pick. Minimum 15 seconds.",
    effect: 'Halves pick timer', cost: 5, rarity: 'common',
    icon: '⏱️', color: 'warning', isActive: true,
    timesUsed: 0, totalCostCollected: 0, createdAt: new Date('2025-03-01'),
  },
  {
    id: 'ds2', name: 'Injury Report',
    description: "Show a random top player as injured (red tag) to all participants for one pick. It's a bluff!",
    effect: 'Fake injury tag', cost: 10, rarity: 'rare',
    icon: '🩹', color: 'damage', isActive: true,
    timesUsed: 0, totalCostCollected: 0, createdAt: new Date('2025-03-01'),
  },
  {
    id: 'ds3', name: 'Snake Reversal',
    description: "Reverse the draft order for the next complete round. Chaos in the snake!",
    effect: 'Reverses next round', cost: 35, rarity: 'legendary',
    icon: '🐍', color: 'damage', isActive: true, enabled: false,
    timesUsed: 0, totalCostCollected: 0, createdAt: new Date('2025-03-01'),
  },
  {
    id: 'ds4', name: 'Pick Skip',
    description: "Opponent loses their next pick (round 10+). If used earlier, it auto-applies at round 10.",
    effect: 'Skips next pick', cost: 15, rarity: 'rare',
    icon: '⏭️', color: 'damage', isActive: true,
    timesUsed: 0, totalCostCollected: 0, createdAt: new Date('2025-03-01'),
  },
  {
    id: 'ds5', name: 'Guess Who',
    description: "Target sees headshots only: names, stats, positions, teams all hidden. List order shuffled. Lasts one pick.",
    effect: 'Headshots only', cost: 10, rarity: 'common',
    icon: '👀', color: 'warning', isActive: true,
    timesUsed: 0, totalCostCollected: 0, createdAt: new Date('2025-03-01'),
  },
  {
    id: 'ds6', name: 'Karma',
    description: "Reverse the last sabotage used against you onto the team that used it. Must activate within 24 hours of purchase. Requires having been sabotaged at least once.",
    effect: 'Reverses last sabotage', cost: 25, rarity: 'rare',
    icon: '🔄', color: 'success', isActive: true, enabled: false,
    timesUsed: 0, totalCostCollected: 0, createdAt: new Date('2025-03-01'),
  },
];

export const defaultDraftSabotageSettings: DraftSabotageSettings = {
  maxActive: 6,
  maxInactive: 0,
  maxPerDraft: 3,
  sabotages: defaultDraftSabotages,
};

// MLB Draft Sabotages
export const mlbDraftSabotages: DraftSabotageConfig[] = [
  {
    id: 'mds1', name: 'Half Time',
    description: "Cut opponent's draft timer in half for their next pick. Minimum 15 seconds.",
    effect: 'Halves pick timer', cost: 5, rarity: 'common',
    icon: '⏱️', color: 'warning', isActive: true,
    timesUsed: 0, totalCostCollected: 0, createdAt: new Date('2025-03-01'),
  },
  {
    id: 'mds2', name: 'Injury Report',
    description: "Show a random top player as injured (red tag) to all participants for one pick. It's a bluff!",
    effect: 'Fake injury tag', cost: 10, rarity: 'rare',
    icon: '🩹', color: 'damage', isActive: true,
    timesUsed: 0, totalCostCollected: 0, createdAt: new Date('2025-03-01'),
  },
  {
    id: 'mds3', name: 'Snake Reversal',
    description: "Reverse the draft order for the next complete round. Chaos in the snake!",
    effect: 'Reverses next round', cost: 35, rarity: 'legendary',
    icon: '🐍', color: 'damage', isActive: true, enabled: false,
    timesUsed: 0, totalCostCollected: 0, createdAt: new Date('2025-03-01'),
  },
  {
    id: 'mds4', name: 'Pick Skip',
    description: "Opponent loses their next pick (round 10+). If used earlier, it auto-applies at round 10.",
    effect: 'Skips next pick', cost: 15, rarity: 'rare',
    icon: '⏭️', color: 'damage', isActive: true,
    timesUsed: 0, totalCostCollected: 0, createdAt: new Date('2025-03-01'),
  },
  {
    id: 'mds5', name: 'Guess Who',
    description: "Target sees headshots only: names, stats, positions, teams all hidden. List order shuffled. Lasts one pick.",
    effect: 'Headshots only', cost: 10, rarity: 'common',
    icon: '👀', color: 'warning', isActive: true,
    timesUsed: 0, totalCostCollected: 0, createdAt: new Date('2025-03-01'),
  },
  {
    id: 'mds6', name: 'Karma',
    description: "Reverse the last sabotage used against you onto the team that used it. Must activate within 24 hours of purchase. Requires having been sabotaged at least once.",
    effect: 'Reverses last sabotage', cost: 25, rarity: 'rare',
    icon: '🔄', color: 'success', isActive: true, enabled: false,
    timesUsed: 0, totalCostCollected: 0, createdAt: new Date('2025-03-01'),
  },
];

export const mlbDraftSabotageSettings: DraftSabotageSettings = {
  maxActive: 6,
  maxInactive: 0,
  maxPerDraft: 3,
  sabotages: mlbDraftSabotages,
};
